/**
 * 3D Carousel Component using React Three Fiber
 *
 * Displays a rotating 3D carousel of collection cards.
 * Uses three.js for 3D rendering and @react-three/drei for helpers.
 *
 * @module containers/FeaturedCarousel/Carousel3D
 */

import { useRef, useState, useEffect, useMemo } from 'react'
import { Canvas, useFrame, ThreeEvent } from '@react-three/fiber'
import { RoundedBox, Text, Center, Environment, ContactShadows, Html } from '@react-three/drei'
import * as THREE from 'three'
import type { Texture, Mesh } from 'three'
import './FeaturedCarousel.css'

/**
 * Carousel item data structure
 */
export interface CarouselItem {
  id: string | number
  image: string
  title: string
  symbol: string
  stats: {
    items: string
    popularity: string
  }
  collection: {
    id: string | number
    name: string
    symbol: string
    contractAddress?: string
    totalSupply?: number
    popScore?: number
    banner?: string
    logo?: string
    imageUrl?: string
    image?: string
    coverImage?: string
    description?: string
    [key: string]: any
  }
}

/**
 * Props for Carousel3D component
 */
interface Carousel3DProps {
  items: CarouselItem[]
  onItemClick: (item: CarouselItem, index: number) => void
  visibleItems?: number
  rotationSpeed?: number
}

/**
 * Load texture with fallback and CORS handling
 * @param url - Image URL
 * @returns Texture | null
 */
function useCollectionTexture(url: string): Texture | null {
  const [texture, setTexture] = useState<Texture | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!url) {
      console.log('useCollectionTexture: empty URL')
      setTexture(null)
      setError(false)
      return
    }

    console.log(`useCollectionTexture: loading ${url}`)
    // Reset error state
    setError(false)

    // Create a temporary image to test loading first
    const img = new Image()
    img.crossOrigin = 'anonymous' // Handle CORS

    img.onload = () => {
      console.log(`useCollectionTexture: image preload success for ${url}`)
      const loader = new THREE.TextureLoader()
      loader.load(
        url,
        (loadedTexture) => {
          console.log(`useCollectionTexture: texture loaded successfully from ${url}`)
          loadedTexture.minFilter = THREE.LinearFilter
          loadedTexture.magFilter = THREE.LinearFilter
          loadedTexture.colorSpace = THREE.SRGBColorSpace
          setTexture(loadedTexture)
        },
        undefined,
        (err) => {
          console.error(`useCollectionTexture: THREE.TextureLoader failed for ${url}`, err)
          setTexture(null)
          setError(true)
        }
      )
    }

    img.onerror = () => {
      console.error(`useCollectionTexture: image preload failed for ${url}`)
      setTexture(null)
      setError(true)
    }

    img.src = url

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [url])

  return texture
}

/**
 * Props for CarouselCard component
 */
interface CarouselCardProps {
  item: CarouselItem
  radius: number
  angle: number
  index: number
  onClick: (item: CarouselItem, index: number) => void
}

/**
 * Individual carousel card component
 */
function CarouselCard({ item, radius, angle, index, onClick }: CarouselCardProps) {
  const meshRef = useRef<Mesh>(null)
  const [hovered, setHover] = useState(false)
  const texture = useCollectionTexture(item.image)

  // Calculate position on the carousel circle
  const x = radius * Math.sin(angle)
  const z = radius * Math.cos(angle)

  // Rotation to face center
  const rotationY = -angle

  useFrame(() => {
    if (meshRef.current) {
      // Smooth animation for hover effect
      if (hovered) {
        meshRef.current.scale.lerp(new THREE.Vector3(1.1, 1.1, 1.1), 0.1)
      } else {
        meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1)
      }
    }
  })

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation()
    // Navigate to collection page
    onClick(item, index)
  }

  // Card dimensions
  const cardWidth = 2.4
  const cardHeight = 3.2
  const cardDepth = 0.1

  // Get a placeholder color if no texture
  const placeholderColor = useMemo(() => {
    const colors = [0x2a4a6e, 0x4a2a6e, 0x6e4a2a, 0x2a6e4a, 0x6e2a4a, 0x4a6e2a]
    return colors[index % colors.length]
  }, [index])

  return (
    <group position={[x, 0, z]} rotation={[0, rotationY, 0]} onClick={handleClick}>
      {/* Shadow beneath card */}
      <mesh
        position={[0, -3.7, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <circleGeometry args={[0.8, 32]} />
        <meshStandardMaterial
          color={hovered ? 0x444444 : 0x000000}
          transparent
          opacity={0.3}
        />
      </mesh>

      {/* Card mesh */}
      <RoundedBox
        ref={meshRef}
        args={[cardWidth, cardHeight, cardDepth]}
        radius={0.1}
        smoothness={4}
      >
        <meshStandardMaterial
          color={hovered ? 0x2a2a2a : 0x1a1a1a}
          metalness={0.1}
          roughness={0.8}
        />
      </RoundedBox>

      {/* FRONT SIDE */}
      <group position={[0, 0, cardDepth / 2 + 0.001]}>
        {/* Invisible click plane covering the whole front */}
        <mesh position={[0, 0, 0]}>
          <planeGeometry args={[cardWidth, cardHeight]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>

        {/* Image plane */}
        <Center position={[0, 0.3, 0]}>
          <mesh>
            <planeGeometry args={[cardWidth - 0.2, cardHeight * 0.5]} />
            <meshStandardMaterial
              transparent
              opacity={0.95}
              side={THREE.DoubleSide}
              color={texture ? '#ffffff' : placeholderColor}
              map={texture || undefined}
            />
          </mesh>
          {!texture && (
            <Text
              position={[0, 0, 0.001]}
              fontSize={0.12}
              color="white"
              anchorX="center"
              anchorY="middle"
              maxWidth={cardWidth - 0.3}
            >
              {item.title.length > 15 ? item.title.slice(0, 15) + '...' : item.title}
            </Text>
          )}
        </Center>

        {/* Collection name */}
        <Text
          position={[0, -cardHeight / 2 + 0.6, 0]}
          fontSize={0.25}
          color="white"
          anchorX="center"
          anchorY="middle"
          maxWidth={cardWidth - 0.3}
        >
          {item.title.length > 20 ? item.title.slice(0, 20) + '...' : item.title}
        </Text>

        {/* Collection symbol */}
        <Text
          position={[0, -cardHeight / 2 + 0.35, 0]}
          fontSize={0.18}
          color="#aaaaaa"
          anchorX="center"
          anchorY="middle"
        >
          {item.symbol}
        </Text>

        {/* Stats - Items count */}
        <Text
          position={[0, -cardHeight / 2 + 0.15, 0]}
          fontSize={0.15}
          color="#4dabf7"
          anchorX="center"
          anchorY="middle"
        >
          {item.stats.items} items
        </Text>

        {/* Stats - Popularity */}
        <Text
          position={[0, -cardHeight / 2 + 0.05, 0]}
          fontSize={0.12}
          color="#ffd43b"
          anchorX="center"
          anchorY="middle"
        >
          ★ {item.stats.popularity}
        </Text>
      </group>

      {/* BACK SIDE - identical to front, rotated 180° */}
      <group position={[0, 0, -cardDepth / 2 - 0.001]} rotation={[0, Math.PI, 0]}>
        {/* Invisible click plane */}
        <mesh position={[0, 0, 0]}>
          <planeGeometry args={[cardWidth, cardHeight]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>

        {/* Image plane */}
        <Center position={[0, 0.3, 0]}>
          <mesh>
            <planeGeometry args={[cardWidth - 0.2, cardHeight * 0.5]} />
            <meshStandardMaterial
              transparent
              opacity={0.95}
              side={THREE.DoubleSide}
              color={texture ? '#ffffff' : placeholderColor}
              map={texture || undefined}
            />
          </mesh>
          {!texture && (
            <Text
              position={[0, 0, 0.001]}
              fontSize={0.12}
              color="white"
              anchorX="center"
              anchorY="middle"
              maxWidth={cardWidth - 0.3}
            >
              {item.title.length > 15 ? item.title.slice(0, 15) + '...' : item.title}
            </Text>
          )}
        </Center>

        {/* Collection name */}
        <Text
          position={[0, -cardHeight / 2 + 0.6, 0]}
          fontSize={0.25}
          color="white"
          anchorX="center"
          anchorY="middle"
          maxWidth={cardWidth - 0.3}
        >
          {item.title.length > 20 ? item.title.slice(0, 20) + '...' : item.title}
        </Text>

        {/* Collection symbol */}
        <Text
          position={[0, -cardHeight / 2 + 0.35, 0]}
          fontSize={0.18}
          color="#aaaaaa"
          anchorX="center"
          anchorY="middle"
        >
          {item.symbol}
        </Text>

        {/* Stats - Items count */}
        <Text
          position={[0, -cardHeight / 2 + 0.15, 0]}
          fontSize={0.15}
          color="#4dabf7"
          anchorX="center"
          anchorY="middle"
        >
          {item.stats.items} items
        </Text>

        {/* Stats - Popularity */}
        <Text
          position={[0, -cardHeight / 2 + 0.05, 0]}
          fontSize={0.12}
          color="#ffd43b"
          anchorX="center"
          anchorY="middle"
        >
          ★ {item.stats.popularity}
        </Text>
      </group>
    </group>
  )
}

/**
 * Props for CarouselScene component
 */
interface CarouselSceneProps {
  items: CarouselItem[]
  radius: number
  onItemClick: (item: CarouselItem, index: number) => void
}

/**
 * Carousel scene component
 */
function CarouselScene({ items, radius, onItemClick }: CarouselSceneProps) {
  const groupRef = useRef<THREE.Group>(null)
  const [targetRotation, setTargetRotation] = useState(0)
  const [currentRotation, setCurrentRotation] = useState(0)
  const [autoRotate, setAutoRotate] = useState(true)

  // Number of items
  const itemCount = items.length
  const angleStep = (2 * Math.PI) / itemCount

  // Auto-rotation
  useEffect(() => {
    if (!autoRotate) return

    let animationFrame: number
    const animate = () => {
      setTargetRotation((prev) => prev + 0.002)
      animationFrame = requestAnimationFrame(animate)
    }
    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [autoRotate])

  // Smooth rotation interpolation
  useEffect(() => {
    const diff = targetRotation - currentRotation
    if (Math.abs(diff) > 0.0001) {
      setCurrentRotation((prev) => prev + diff * 0.05)
    }
  }, [targetRotation, currentRotation])

  // Handle mouse wheel for manual rotation
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      setAutoRotate(false)
      setTargetRotation((prev) => prev - e.deltaY * 0.001)
    }

    const canvas = document.querySelector('#carousel-canvas')
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false })
      return () => canvas.removeEventListener('wheel', handleWheel)
    }
  }, [])

  // Handle canvas click to pause auto-rotation temporarily
  useEffect(() => {
    let timeout: NodeJS.Timeout
    const handleClick = () => {
      setAutoRotate(false)
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        setAutoRotate(true)
      }, 5000)
    }

    const canvas = document.querySelector('#carousel-canvas')
    if (canvas) {
      canvas.addEventListener('click', handleClick)
      return () => canvas.removeEventListener('click', handleClick)
    }
  }, [])

  return (
    <group ref={groupRef} rotation={[0.3, currentRotation, 0]}>
      {items.map((item, index) => {
        const angle = index * angleStep
        return (
          <CarouselCard
            key={item.id}
            item={item}
            radius={radius}
            angle={angle}
            index={index}
            onClick={onItemClick}
          />
        )
      })}
    </group>
  )
}

/**
 * Main 3D Carousel component
 */
export default function Carousel3D({ items, onItemClick, visibleItems = 5, rotationSpeed = 0.5 }: Carousel3DProps) {
  // Calculate carousel radius based on item count
  const radius = useMemo(() => {
    // Adjust radius so items don't overlap too much
    // For visibleItems = 5, we want the arc to be comfortable
    const baseRadius = 5
    const angleStep = (2 * Math.PI) / items.length
    // Wider spacing for fewer visible items
    const spacing = Math.max(1.2, 2.5 / visibleItems)
    return baseRadius * spacing
  }, [items.length, visibleItems])

  if (items.length === 0) {
    return <div className="text-white">No items to display</div>
  }

  return (
    <div
      id="carousel-canvas"
      style={{
        width: '100%',
        height: '500px',
        cursor: 'grab',
      }}
    >
      <Canvas
        camera={{
          position: [0, 0, radius + 3],
          fov: 50,
          near: 0.1,
          far: 100,
        }}
        style={{ background: 'transparent' }}
        dpr={[1, 2]}
      >
        {/* Lighting */}
        <ambientLight intensity={0.5} />
        <spotLight
          position={[10, 10, 10]}
          angle={0.15}
          penumbra={1}
          shadow-mapSize={[2048, 2048]}
        />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />

        {/* Environment for reflections */}
        <Environment preset="city" />

        {/* Carousel */}
        <CarouselScene
          items={items}
          radius={radius}
          onItemClick={onItemClick}
        />

        {/* Contact shadows for realistic floor shadows */}
        <ContactShadows
          position={[0, -4, 0]}
          opacity={0.6}
          scale={20}
          blur={2}
          far={4}
        />

        {/* Camera controls hint */}
        <Html position={[0, 2, radius + 2]} center>
          <div className="carousel-hint">
            Scroll to rotate
          </div>
        </Html>
      </Canvas>
    </div>
  )
}
